import { useState } from "react";
import { motion } from "framer-motion";
import { Home, Video, MessageCircle, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];

const MODES = [
  {
    id: "in_home_punjab",
    icon: Home,
    title: "Inside Punjab",
    desc: "I come to your home — in-home trials & wedding outfit styling.",
  },
  {
    id: "online",
    icon: Video,
    title: "Outside Punjab",
    desc: "HD virtual consultation, swatch delivery & complete online styling.",
  },
];

const CATEGORIES = ["Groom Styling", "Family Styling", "Personal Styling"];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  mode: "in_home_punjab",
  category: "Groom Styling",
  date: "",
  message: "",
};

export default function Booking() {
  const [form, setForm] = useState(initialForm);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone number are required.");
      return;
    }
    const lines = [
      "Hello Manvir, I want to book a styling appointment.",
      "",
      `Name: ${form.name}`,
      `Contact: ${form.phone}`,
      form.email.trim() && `Email: ${form.email}`,
      `Styling: ${form.category}`,
      `Mode: ${
        form.mode === "in_home_punjab"
          ? "Inside Punjab (In-home visit)"
          : "Outside Punjab (Online consultation)"
      }`,
      form.date && `Event Date: ${form.date}`,
      form.message.trim() && `Details: ${form.message}`,
    ].filter(Boolean);
    window.open(
      `https://wa.me/918801387013?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank"
    );
    toast.success("Opening WhatsApp — just press send!");
    setForm(initialForm);
  };

  const inputCls =
    "w-full border border-[#E5DEC9] bg-[#FFFFFF] px-5 py-3.5 text-sm text-[#141414] placeholder-[#686152]/60 outline-none transition-colors duration-300 focus:border-[#C5A059]";

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="bg-[#FDFBF7] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold uppercase tracking-[0.35em] text-[#C5A059] lg:col-span-3"
          >
            Book Appointment
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-serif text-3xl font-medium leading-tight tracking-tight text-[#141414] sm:text-4xl lg:col-span-9 lg:text-5xl"
          >
            Your Perfect Look,{" "}
            <span className="italic text-[#C5A059]">One Appointment</span> Away.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            onSubmit={submit}
            data-testid="booking-form"
            className="space-y-6 lg:col-span-7"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {MODES.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  data-testid={`booking-mode-${m.id === "online" ? "online" : "inhome"}`}
                  onClick={() => setForm({ ...form, mode: m.id })}
                  className={`border p-6 text-left transition-all duration-300 ${
                    form.mode === m.id
                      ? "border-[#C5A059] bg-[#C5A059]/10"
                      : "border-[#E5DEC9] hover:border-[#C5A059]/60"
                  }`}
                >
                  <m.icon
                    size={22}
                    className={form.mode === m.id ? "text-[#C5A059]" : "text-[#686152]"}
                  />
                  <p className="mt-3 font-serif text-xl text-[#141414]">{m.title}</p>
                  <p className="mt-2 text-xs leading-relaxed text-[#686152]">{m.desc}</p>
                </button>
              ))}
            </div>

            <p
              data-testid="booking-summary"
              className="border-l-2 border-[#C5A059] pl-4 text-xs uppercase tracking-[0.2em] text-[#686152]"
            >
              {form.mode === "in_home_punjab"
                ? "In-home visit — Punjab · " + form.category
                : "Online consultation — worldwide · " + form.category}
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                data-testid="booking-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Your Name *"
                className={inputCls}
              />
              <input
                data-testid="booking-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone Number *"
                type="tel"
                className={inputCls}
              />
              <input
                data-testid="booking-email-input"
                value={form.email}
                onChange={set("email")}
                placeholder="Email (optional)"
                type="email"
                className={inputCls}
              />
              <input
                data-testid="booking-date-input"
                value={form.date}
                onChange={set("date")}
                type="date"
                className={inputCls}
              />
            </div>

            <div className="flex flex-wrap gap-3" data-testid="booking-categories">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`booking-category-${c.split(" ")[0].toLowerCase()}`}
                  onClick={() => setForm({ ...form, category: c })}
                  className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    form.category === c
                      ? "bg-[#C5A059] text-[#0C0C0C]"
                      : "border border-[#E5DEC9] text-[#686152] hover:border-[#C5A059]/60 hover:text-[#141414]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <textarea
              data-testid="booking-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder="Event details — wedding date, venue, number of people, any preferences..."
              rows={4}
              className={`${inputCls} resize-none`}
            />

            <button
              data-testid="booking-submit-btn"
              type="submit"
              className="flex w-full items-center justify-center gap-3 rounded-full bg-[#C5A059] py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#0C0C0C] transition-colors duration-300 hover:bg-[#B38C43] sm:w-auto sm:px-12"
            >
              <MessageCircle size={15} />
              Send on WhatsApp
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="lg:col-span-5"
          >
            <div className="border border-[#E5DEC9] bg-[#FFFFFF] p-8 lg:p-12">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
                Direct Contact
              </p>
              <h3 className="mt-4 font-serif text-2xl text-[#141414] sm:text-3xl">
                In a hurry? Talk directly.
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#686152]">
                Fix your appointment over WhatsApp or a call — a home visit in
                Punjab, or an online session from anywhere in the world.
              </p>

              <div className="mt-10 space-y-4">
                <a
                  data-testid="booking-whatsapp-btn"
                  href="https://wa.me/918801387013?text=Hello%20Manvir%2C%20I%20want%20to%20book%20a%20styling%20appointment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 rounded-full bg-[#25D366] py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#0C0C0C] transition-opacity duration-300 hover:opacity-85"
                >
                  <MessageCircle size={16} />
                  Chat on WhatsApp
                </a>
                <a
                  data-testid="booking-call-btn"
                  href="tel:+918801387013"
                  className="flex items-center justify-center gap-3 rounded-full border border-[#141414]/25 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#141414] transition-colors duration-300 hover:border-[#C5A059] hover:text-[#B38C43]"
                >
                  <Phone size={16} />
                  +91 88013 87013
                </a>
                <a
                  data-testid="booking-email-btn"
                  href="mailto:mrveer.in7@gmail.com"
                  className="flex items-center justify-center gap-3 rounded-full border border-[#141414]/25 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#141414] transition-colors duration-300 hover:border-[#C5A059] hover:text-[#B38C43]"
                >
                  <Mail size={16} />
                  mrveer.in7@gmail.com
                </a>
              </div>

              <div className="mt-10 space-y-3 border-t border-[#E5DEC9] pt-8 text-sm text-[#686152]">
                <p data-testid="contact-instagram">
                  <span className="text-[#141414]">Instagram:</span>{" "}
                  <a
                    href="https://instagram.com/mrveer.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-4 hover:underline"
                  >
                    @mrveer.in
                  </a>
                </p>
                <p data-testid="contact-email">
                  <span className="text-[#141414]">Email:</span>{" "}
                  <a href="mailto:mrveer.in7@gmail.com" className="underline-offset-4 hover:underline">
                    mrveer.in7@gmail.com
                  </a>
                </p>
                <p data-testid="contact-location">
                  <span className="text-[#141414]">Service Area:</span> Punjab (in-home)
                  · Worldwide (online)
                </p>
                <p data-testid="contact-hours">
                  <span className="text-[#141414]">Note:</span> By appointment only —
                  no walk-ins.
                </p>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
